/**
 * Best-effort identity document field extraction from OCR/text (multi-language).
 */

const EXPIRY_LABEL_RE =
  /(?:expir(?:y|ation|es|a)?|valid\s*(?:until|thru|through|ade|ità)|date\s*of\s*expir(?:y|ation)|vencimento|validade|scadenza|fecha\s*de\s*vencimiento|gültig\s*bis|valable\s*jusqu)\s*[:\s]*(\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}|\d{4}[/.-]\d{1,2}[/.-]\d{1,2})/gi;

const DOC_NUMBER_LABEL_RE =
  /(?:document\s*(?:no|number|#|o)?|passport\s*(?:no|number|#)?|id\s*(?:no|number|#)?|n[ºo°\.]?\s*(?:do\s*)?(?:documento|identidade|registro)|carteira|licen[cs]a|carta\s*di\s*identit[aà]|codice\s*fiscale|cpf|rg|registro\s*geral|número)\s*[:\s#]*([A-Z0-9][A-Z0-9\s./-]{4,24})/gi;

const DOB_LABEL_RE =
  /(?:date\s*of\s*birth|d\.?\s*o\.?\s*b\.?|birth\s*date|born|data\s*de\s*nascimento|nascimento|fecha\s*de\s*nacimiento|data\s*di\s*nascita|geburtsdatum|geboren|date\s*de\s*naissance|n[ée]e?\s*le)\s*[:\s]*(\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}|\d{4}[/.-]\d{1,2}[/.-]\d{1,2})/gi;

const MAX_HUMAN_AGE_MS = 120 * 365.25 * 24 * 60 * 60 * 1000;

const BR_RG_RE = /\b\d{1,2}\.?\d{3}\.?\d{3}[-.]?[0-9Xx]\b/;
const BR_CNH_RE = /\b\d{11}\b/;
const EU_ID_RE = /\b[A-Z]{2}\d{6,9}\b/;
const IT_CF_RE = /\b[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]\b/i;

function parseMrzTd3(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((l) => l.replace(/\s/g, "").toUpperCase())
    .filter((l) => l.length >= 30);

  const line2Candidates = lines.filter((l) => /^[A-Z0-9<]{30,}$/.test(l) && !l.startsWith("P<"));
  for (const l2 of line2Candidates) {
    const padded = (l2 + "<".repeat(44)).slice(0, 44);
    const docNum = padded.slice(0, 9).replace(/</g, "").trim();
    const expiryYyMmDd = padded.slice(21, 27);
    if (/^\d{6}$/.test(expiryYyMmDd)) {
      const yy = Number(expiryYyMmDd.slice(0, 2));
      const mm = expiryYyMmDd.slice(2, 4);
      const dd = expiryYyMmDd.slice(4, 6);
      const yyyy = yy >= 50 ? 1900 + yy : 2000 + yy;
      const iso = `${yyyy}-${mm}-${dd}`;
      if (docNum.length >= 5) {
        // Date of birth lives at fixed positions 13–18 (YYMMDD) on MRZ line 2.
        const date_of_birth = mrzYyMmDdToDob(padded.slice(13, 19));
        return {
          document_number: docNum,
          expiry_date: iso,
          date_of_birth,
          confidence: "high",
          method: "mrz_td3",
        };
      }
    }
  }
  return null;
}

/** MRZ birth date (YYMMDD) → ISO. Year pivot assumes a past date (person already born). */
function mrzYyMmDdToDob(yyMmDd) {
  if (!/^\d{6}$/.test(yyMmDd)) return null;
  const yy = Number(yyMmDd.slice(0, 2));
  const mm = Number(yyMmDd.slice(2, 4));
  const dd = Number(yyMmDd.slice(4, 6));
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  const currentYy = new Date().getFullYear() % 100;
  const yyyy = yy > currentYy ? 1900 + yy : 2000 + yy;
  return `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
}

/** Normalize a labeled DOB (day-first for BR/EU docs; 2-digit years pivot to the past). */
export function normalizeDobToIso(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const currentYy = new Date().getFullYear() % 100;
  const toFullYear = (y) => {
    if (y.length === 4) return Number(y);
    const yy = Number(y);
    return yy > currentYy ? 1900 + yy : 2000 + yy;
  };
  const dmy = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (dmy) {
    const dd = dmy[1].padStart(2, "0");
    const mm = dmy[2].padStart(2, "0");
    return `${toFullYear(dmy[3])}-${mm}-${dd}`;
  }
  const ymd = s.match(/^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/);
  if (ymd) {
    return `${ymd[1]}-${ymd[2].padStart(2, "0")}-${ymd[3].padStart(2, "0")}`;
  }
  return null;
}

/** Best-effort DOB from labeled text (non-MRZ docs). Must be a plausible past human birth date. */
function pickBestDob(text) {
  const matches = [...String(text).matchAll(DOB_LABEL_RE)];
  for (const m of matches) {
    const iso = normalizeDobToIso(m[1]);
    if (!iso) continue;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) continue;
    if (t < Date.now() && t > Date.now() - MAX_HUMAN_AGE_MS) return iso;
  }
  return null;
}

export function normalizeExpiryToIso(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dmy = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (dmy) {
    const dd = dmy[1].padStart(2, "0");
    const mm = dmy[2].padStart(2, "0");
    return `${dmy[3]}-${mm}-${dd}`;
  }
  const ymd = s.match(/^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/);
  if (ymd) {
    return `${ymd[1]}-${ymd[2].padStart(2, "0")}-${ymd[3].padStart(2, "0")}`;
  }
  const dt = new Date(s);
  if (!Number.isNaN(dt.getTime())) return dt.toISOString().slice(0, 10);
  return null;
}

function pickBestExpiry(text) {
  const expiryMatches = [...String(text).matchAll(EXPIRY_LABEL_RE)];
  if (expiryMatches.length) {
    return normalizeExpiryToIso(expiryMatches[expiryMatches.length - 1][1]);
  }

  const futureDates = [];
  const re = /\b(\d{1,2}[/.-]\d{1,2}[/.-]\d{4}|\d{4}[/.-]\d{1,2}[/.-]\d{1,2})\b/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    const iso = normalizeExpiryToIso(m[1]);
    if (!iso) continue;
    const t = new Date(iso).getTime();
    if (t > Date.now() - 86400000 && t < Date.now() + 25 * 365.25 * 86400000) futureDates.push(iso);
  }
  if (futureDates.length) {
    futureDates.sort();
    return futureDates[futureDates.length - 1];
  }
  return null;
}

const OCR_NOISE_RE = /proibido|plastificar|republica|federativa|brasil|ministerio|detran|assinatura|foto/i;

function isPlausibleDocNumber(value) {
  if (!value || value.length < 5) return false;
  if (OCR_NOISE_RE.test(value)) return false;
  if (value.split(/\s+/).length > 4) return false;
  return true;
}

function pickBestDocNumber(text) {
  const docMatches = [...String(text).matchAll(DOC_NUMBER_LABEL_RE)];
  for (const m of docMatches) {
    const raw = m[1].replace(/\s+/g, " ").trim().toUpperCase();
    if (isPlausibleDocNumber(raw)) return raw;
  }

  const mrzNum = parseMrzTd3(text)?.document_number;
  if (mrzNum) return mrzNum;

  const brRg = text.match(BR_RG_RE);
  if (brRg) return brRg[0].replace(/\s/g, "");

  const itCf = text.match(IT_CF_RE);
  if (itCf) return itCf[0].toUpperCase();

  const eu = text.match(EU_ID_RE);
  if (eu) return eu[0];

  const cnh = text.match(BR_CNH_RE);
  if (cnh && text.toLowerCase().includes("cnh")) return cnh[0];

  const passportLike = text.match(/\b[A-Z]{1,3}\d{6,9}\b/);
  if (passportLike && isPlausibleDocNumber(passportLike[0])) return passportLike[0];

  return null;
}

export function extractIdentityHintsFromText(text) {
  if (!text || !String(text).trim()) {
    return { document_number: null, expiry_date: null, date_of_birth: null, confidence: "none", method: "empty" };
  }

  const mrz = parseMrzTd3(text);
  if (mrz) return mrz;

  const expiry_date = pickBestExpiry(text);
  const document_number = pickBestDocNumber(text);
  const date_of_birth = pickBestDob(text);
  let method = "heuristic";
  if (document_number && expiry_date) method = "multi_lang_heuristic";

  const confidence =
    document_number && expiry_date ? "medium" : document_number || expiry_date ? "low" : "none";

  return { document_number, expiry_date, date_of_birth, confidence, method };
}
